import { Select as ChakraSelect } from "@chakra-ui/react";
import { Skeleton } from "@chakra-ui/react";
import { useCommand } from "../hooks/commands.hooks";
import OrderStatusBadge from "./OrderStatusBadge";
import { CommandStatus } from "../types/command.types";
import i18n from "../i18n";
import { useMutation, useQueryClient } from "react-query";
import {
	changeCommandStatus,
	updateCommand,
} from "../mutations/command.mutations";
import { useConnectedUser } from "../hooks/user.hooks";
import { printLabels } from "../hooks/printers.hooks";

type CommandStatusBadgeReadOnlyProps = {
	id: number | string | null;
};

type CommandStatusBadgeWriteProps = CommandStatusBadgeReadOnlyProps;

type CommandStatusBadgeProps = CommandStatusBadgeReadOnlyProps & {
	editable?: boolean;
};

const mapping = new Map<string, [string, string, string, string]>([
	[CommandStatus.DRAFT, ["#FFFFFF", "#1E293B", "#334155", "Brouillon"]],
	[
		CommandStatus.WAITING_FOR_RECEPTION,
		["#334155", "#E2E8F0", "#F8FAFC", "En attente de réception"],
	],
	[CommandStatus.RECEIVED, ["#000000", "#BCD9FF", "#D9E9FF", "Réceptionnée"]],
	[
		CommandStatus.MODELING,
		["#000000", "#85E13A", "#A6EF67", "En modélisation"],
	],
	[CommandStatus.PRINTING, ["#000000", "#4ADE80", "#4ADE80", "En impression"]],
	[
		CommandStatus.INJECTING,
		["#FFFFFF", "#16A34A", "#16A34A", "En injection silicone"],
	],
	[CommandStatus.POLISHING, ["#FFFFFF", "#087443", "#326212", "En finition"]],
	[
		CommandStatus.QUALITY_CONTROL,
		["#087443", "#326212", "#FFFFFF", "Contrôle qualité"],
	],
	[
		CommandStatus.READY_FOR_DELIVERY,
		["#FFFFFF", "#2B75FF", "#599FFF", "Prête à livrer"],
	],
	[CommandStatus.IN_DELIVERY, ["#FFFFFF", "#1444E1", "#1B58F5", "Expédiée"]],
	[CommandStatus.ABORTED, ["#FFFFFF", "#B91C1C", "#DC2626", "Annulée"]],
	[CommandStatus.PENDING, ["#FFFFFF", "#C2410C", "#F68511", "En pause"]],
	[CommandStatus.DELIVERED, ["#FFFFFF", "#142157", "#19338F", "Livrée"]],
	["HALTED", ["#FFFFFF", "#C2410C", "#F68511", "En pause"]],
]);

const CommandStatusBadgeReadOnly = ({
	id,
}: CommandStatusBadgeReadOnlyProps) => {
	const { data: command, isLoading } = useCommand(id);
	const status = command?.haltedAt ? "HALTED" : command?.status;

	if (isLoading) {
		return <Skeleton w="10rem" h="20px" />;
	}
	if (!command || status === undefined) {
		return null;
	}
	return (
		<OrderStatusBadge
			halted={command.haltedAt != null}
			status={status as CommandStatus & "HALTED"}
		></OrderStatusBadge>
	);
};

const CommandStatusBadgeWrite = ({ id }: CommandStatusBadgeWriteProps) => {
	const { data: command, isLoading } = useCommand(id);
	const status = command?.haltedAt ? "HALTED" : command?.status;
	const statusMapping = mapping.get(status ?? "");
	const user = useConnectedUser();
	const queryClient = useQueryClient();
	const { mutate: mutateCommandUpdate } = useMutation(updateCommand, {
		onSuccess: async (data) => {
			await queryClient.invalidateQueries();
			if (data.status === CommandStatus.READY_FOR_DELIVERY && data.id) {
				await printLabels([data.id], "Imprimante Etiquettes");
			}
		},
	});
	const { mutate: mutateCommandStatus } = useMutation(changeCommandStatus, {
		onSuccess: async () => {
			if (command?.id) {
				await queryClient.invalidateQueries();
				await queryClient.invalidateQueries(["command", command?.id]);
			}
		},
	});
	const handleHalt = (id: number) => {
		if (user?.isSuperAdmin) {
			mutateCommandStatus({
				id,
				status: "HALTED",
				reason: "",
				explanation: "",
			});
		}
	};

	if (isLoading) {
		return <Skeleton w="10rem" h="20px" />;
	}
	if (!command || !command.id) {
		return null;
	}
	return (
		<ChakraSelect
			w="fit-content"
			color={statusMapping ? statusMapping[0] : "color.700"}
			bgColor={statusMapping ? statusMapping[2] : "color.100"}
			borderColor={statusMapping ? statusMapping[1] : "color.700"}
			h="30px"
			fontSize="14px"
			borderRadius="xl"
			value={status}
			onChange={(event) => {
				if (event.target.value === "HALTED") {
					handleHalt(command.id);
				} else {
					mutateCommandUpdate({
						id: command.id,
						status: event.target.value as CommandStatus,
					});
				}
			}}
		>
			<option style={{ color: "black" }} value="">
				---
			</option>
			{[
				CommandStatus.DRAFT,
				CommandStatus.WAITING_FOR_RECEPTION,
				CommandStatus.RECEIVED,
				CommandStatus.MODELING,
				CommandStatus.PRINTING,
				CommandStatus.INJECTING,
				CommandStatus.POLISHING,
				CommandStatus.QUALITY_CONTROL,
				CommandStatus.READY_FOR_DELIVERY,
				CommandStatus.IN_DELIVERY,
				CommandStatus.DELIVERED,
			].map((status) => (
				<option key={status} style={{ color: "black" }} value={status}>
					{i18n.t(`command.status.${status}`)}
				</option>
			))}
			<option style={{ color: "black" }} value="HALTED">
				En pause
			</option>
			<option style={{ color: "black" }} value={CommandStatus.ABORTED}>
				{i18n.t(`command.status.${CommandStatus.ABORTED}`)}
			</option>
		</ChakraSelect>
	);
};

const CommandStatusBadge = ({
	editable = false,
	...props
}: CommandStatusBadgeProps) => {
	if (editable) {
		return <CommandStatusBadgeWrite {...props} />;
	}
	return <CommandStatusBadgeReadOnly {...props} />;
};

export default CommandStatusBadge;
